import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  CheckCircleOutline as ResolveIcon,
  DeleteOutline as TrashIcon,
  EditOutlined as EditIcon,
  Close as CloseIcon,
  UndoOutlined as UndoIcon,
} from "@mui/icons-material";
import { dt } from "../themes/redesignTokens";

// ─── Types ──────────────────────────────────────────────────

export type ToastVariant = "resolve" | "delete" | "edit" | "error" | "success" | "undo";

export interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
  duration: number;
  action?: React.ReactNode;
}

interface ActionToastProps {
  open: boolean;
  variant: ToastVariant;
  message: string;
  onClose: () => void;
  /** Auto-dismiss in ms (default 5000) */
  duration?: number;
  /** Optional action slot (e.g. Undo button) */
  action?: React.ReactNode;
}

// ─── Variant config ────────────────────────────────────────

const VARIANT_CONFIG: Record<
  ToastVariant,
  { accentColor: string; bgColor: string; icon: React.ReactNode }
> = {
  resolve: {
    accentColor: "#2D8C4E",
    bgColor: "#FFFFFF",
    icon: <ResolveIcon sx={{ fontSize: 16, color: "#2D8C4E" }} />,
  },
  delete: {
    accentColor: "#3D3D3D",
    bgColor: "#3D3D3D",
    icon: <TrashIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />,
  },
  edit: {
    accentColor: "#D4572B",
    bgColor: "#FFFFFF",
    icon: <EditIcon sx={{ fontSize: 16, color: "#D4572B" }} />,
  },
  error: {
    accentColor: "#DC2626",
    bgColor: "#FFFFFF",
    icon: <CloseIcon sx={{ fontSize: 16, color: "#DC2626" }} />,
  },
  success: {
    accentColor: "#2D8C4E",
    bgColor: "#FFFFFF",
    icon: <ResolveIcon sx={{ fontSize: 16, color: "#2D8C4E" }} />,
  },
  undo: {
    accentColor: "#3B82F6",
    bgColor: "#FFFFFF",
    icon: <UndoIcon sx={{ fontSize: 16, color: "#3B82F6" }} />,
  },
};

const MAX_VISIBLE_TOASTS = 5;
const TOAST_GAP = 10;
/** L6 (USER-LANG-IT-PL-1): never wider than the viewport minus the 16px side gutters. */
const TOAST_MAX_WIDTH = "min(400px, calc(100vw - 32px))";
const TOAST_MIN_WIDTH = "min(280px, calc(100vw - 32px))";

// ─── Inject keyframes once ──────────────────────────────────

let keyframesInjected = false;
function injectKeyframes() {
  if (keyframesInjected) return;
  const id = "sr-toast-keyframes";
  if (document.getElementById(id)) { keyframesInjected = true; return; }
  const style = document.createElement("style");
  style.id = id;
  style.textContent = `
    @keyframes srToastSlideIn {
      0% { transform: translateX(120%); opacity: 0; }
      100% { transform: translateX(0); opacity: 1; }
    }
    @keyframes srToastSlideOut {
      0% { transform: translateX(0); opacity: 1; }
      100% { transform: translateX(120%); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
  keyframesInjected = true;
}

// ─── Single toast item renderer ─────────────────────────────

interface ToastItemRendererProps {
  item: ToastItem;
  onDismiss: (id: number) => void;
}

const ToastItemRenderer: React.FC<ToastItemRendererProps> = ({ item, onDismiss }) => {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setExiting(true);
    }, item.duration);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [item.duration]);

  useEffect(() => {
    if (!exiting) return;
    const exitTimer = setTimeout(() => onDismiss(item.id), 250);
    return () => clearTimeout(exitTimer);
  }, [exiting, item.id, onDismiss]);

  const handleClose = useCallback(() => {
    setExiting(true);
  }, []);

  const config = VARIANT_CONFIG[item.variant];
  const isDark = item.variant === "delete";

  // Positioned by the ToastStack column (no per-index fixed `top`), so a message that wraps
  // to two lines pushes the next toast down instead of overlapping it.
  return (
    <Box
      data-testid="action-toast"
      sx={{
        animation: exiting
          ? "srToastSlideOut 0.25s ease forwards"
          : "srToastSlideIn 0.3s ease",
        pointerEvents: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          backgroundColor: config.bgColor,
          borderRadius: "10px",
          borderLeft: `3px solid ${config.accentColor}`,
          boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
          padding: "12px 16px",
          minWidth: TOAST_MIN_WIDTH,
          maxWidth: TOAST_MAX_WIDTH,
        }}
      >
        {/* Icon */}
        <Box sx={{ display: "flex", flexShrink: 0 }}>
          {config.icon}
        </Box>

        {/* Message */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowWrap: "break-word",
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 500,
            color: isDark ? "#FFFFFF" : "#0C1117",
          }}
        >
          {item.message}
        </Box>

        {/* Action slot (e.g. Undo button) */}
        {item.action && (
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            {item.action}
          </Box>
        )}

        {/* Close button */}
        <Box
          onClick={handleClose}
          sx={{
            display: "flex",
            cursor: "pointer",
            flexShrink: 0,
            opacity: 0.5,
            transition: "opacity 0.15s ease",
            "&:hover": { opacity: 1 },
          }}
        >
          <CloseIcon sx={{ fontSize: 16, color: isDark ? "#FFFFFF" : "#8C9099" }} />
        </Box>
      </Box>
    </Box>
  );
};

// ─── Toast Stack component ──────────────────────────────────

interface ToastStackProps {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}

export const ToastStack: React.FC<ToastStackProps> = ({ toasts, onDismiss }) => {
  useEffect(() => { injectKeyframes(); }, []);

  if (toasts.length === 0) return null;

  // L6 (USER-LANG-IT-PL-1): one fixed column instead of per-toast `top = index * 52px`.
  // Longer localized messages wrap to two lines (~62px), which made stacked toasts overlap.
  return (
    <Box
      sx={{
        position: "fixed",
        top: "16px",
        right: "16px",
        zIndex: 1500,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: `${TOAST_GAP}px`,
        pointerEvents: "none",
      }}
    >
      {toasts.map((toast) => (
        <ToastItemRenderer
          key={toast.id}
          item={toast}
          onDismiss={onDismiss}
        />
      ))}
    </Box>
  );
};

// ─── useToastStack hook ─────────────────────────────────────

let toastIdCounter = 0;

export function useToastStack() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (
      variant: ToastVariant,
      message: string,
      options?: { duration?: number; action?: React.ReactNode }
    ): number => {
      const id = ++toastIdCounter;
      const newToast: ToastItem = {
        id,
        variant,
        message,
        duration: options?.duration ?? 5000,
        action: options?.action,
      };
      setToasts((prev) => {
        const next = [...prev, newToast];
        // Cap visible toasts — remove oldest if over limit
        if (next.length > MAX_VISIBLE_TOASTS) {
          return next.slice(next.length - MAX_VISIBLE_TOASTS);
        }
        return next;
      });
      return id;
    },
    []
  );

  return { addToast, dismissToast, toasts };
}

// ─── Legacy single-toast component (backward compat) ────────

const ActionToast: React.FC<ActionToastProps> = ({
  open,
  variant,
  message,
  onClose,
  duration = 5000,
  action,
}) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => { injectKeyframes(); }, []);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setExiting(false);
    } else if (visible) {
      setExiting(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setExiting(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [open, visible]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      onClose();
    }, duration);
    return () => clearTimeout(timer);
  }, [open, duration, onClose]);

  if (!visible) return null;

  const config = VARIANT_CONFIG[variant];
  const isDark = variant === "delete";

  return (
    <Box
      data-testid="action-toast"
      sx={{
        position: "fixed",
        top: "16px",
        right: "16px",
        zIndex: 1500,
        animation: exiting
          ? "srToastSlideOut 0.25s ease forwards"
          : "srToastSlideIn 0.3s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          backgroundColor: config.bgColor,
          borderRadius: "10px",
          borderLeft: `3px solid ${config.accentColor}`,
          boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
          padding: "12px 16px",
          minWidth: TOAST_MIN_WIDTH,
          maxWidth: TOAST_MAX_WIDTH,
        }}
      >
        {/* Icon */}
        <Box sx={{ display: "flex", flexShrink: 0 }}>
          {config.icon}
        </Box>

        {/* Message */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowWrap: "break-word",
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 500,
            color: isDark ? "#FFFFFF" : "#0C1117",
          }}
        >
          {message}
        </Box>

        {/* Action slot (e.g. Undo button) */}
        {action && (
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            {action}
          </Box>
        )}

        {/* Close button */}
        <Box
          onClick={onClose}
          sx={{
            display: "flex",
            cursor: "pointer",
            flexShrink: 0,
            opacity: 0.5,
            transition: "opacity 0.15s ease",
            "&:hover": { opacity: 1 },
          }}
        >
          <CloseIcon sx={{ fontSize: 16, color: isDark ? "#FFFFFF" : "#8C9099" }} />
        </Box>
      </Box>
    </Box>
  );
};

export default ActionToast;
